/**
 * @name 可编辑表格组件-基于行的
 * @author 吴迪 di.c.wu@pwc.com
 * @link https://4x.ant.design/components/table/#components-table-demo-edit-row
 *
 * @version 0.1.2
 * @createdTime 2024-8-18
 * @lastModifiedTime 2024-XX-XX
 * @lastModifiedUser [Your Name]
 *
 * @changelog
 * v0.1.2 - Added ref support for external form control, added validatingEmailIndex for loading state
 *        - All changes are backward compatible
 */
import {
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Typography,
  Checkbox,
  Select,
} from 'antd';
import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { PlusCircleFilled, LoadingOutlined } from '@ant-design/icons';
import './index.scss';

// Stable component for email input with onEmailChange - defined at module level so
// React does not unmount/remount on parent re-renders (avoids focus loss when typing)
const EmailInputWithChange = ({
  value,
  onChange,
  record,
  index,
  onEmailChangeProp,
  form,
  title,
  saveFlag,
  isValidating,
  restComProps,
}) => (
  <Input
    style={{ minHeight: 46 }}
    {...restComProps}
    value={value}
    onChange={(e) => {
      onChange?.(e);
      onEmailChangeProp?.(
        { ...record, email: e.target.value },
        index,
      );
    }}
    disabled={
      (title === 'Email' && record?.email && saveFlag ? true : false) ||
      isValidating
    }
    suffix={
      isValidating ? (
        <LoadingOutlined style={{ color: '#1890ff' }} />
      ) : (
        restComProps?.suffix ?? null
      )
    }
    onBlur={() => {
      if (restComProps?.onBlur) {
        restComProps.onBlur(
          { ...record, ...form?.getFieldsValue?.() },
          index,
        );
      }
    }}
  />
);

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType = 'input',
  record,
  saveRow,
  index,
  rules,
  customRender,
  comProps = {},
  form,
  children,
  saveFlag,
  validatingEmailIndex = null, // NEW: default null for backward compatibility
  ...restProps
}) => {
  // NEW: Check if this specific cell is being validated
  // Will be false for all existing usages since validatingEmailIndex defaults to null
  const isValidating =
    validatingEmailIndex !== null &&
    validatingEmailIndex === index &&
    dataIndex === 'email';

  const inputNode =
    customRender ||
    {
      userSelect: <Select {...comProps} />,
      select: <Select {...comProps} />,
      checkbox: (
        <Checkbox
          {...comProps}
          onChange={(e) => {
            form.setFieldsValue({ [dataIndex]: e.target.checked });
          }}
        />
      ),
      input: (
        <Input
          style={{ minHeight: 46 }}
          {...comProps}
          // MODIFIED: Added isValidating check - when false (default), behavior unchanged
          disabled={
            (title === 'Email' && record.email && saveFlag ? true : false) ||
            isValidating
          }
          // NEW: Show loading indicator only when validating - null otherwise
          suffix={
            isValidating ? (
              <LoadingOutlined style={{ color: '#1890ff' }} />
            ) : (
              comProps.suffix || null
            )
          }
          onBlur={() => {
            if (comProps.onBlur) {
              // MODIFIED: Pass index as second parameter for new usage
              // Existing onBlur handlers that don't use index will still work
              comProps.onBlur({ ...record, ...form.getFieldsValue() }, index);
            }
          }}
        />
      ),
      number: <InputNumber {...comProps} />,
    }[inputType];

  // Email with onEmailChange: use stable wrapper so Form.Item injects value/onChange via cloneElement
  const isEmailWithOnChange =
    dataIndex === 'email' && comProps.onEmailChange;

  const emailInputNode =
    isEmailWithOnChange && (() => {
      const { onEmailChange: onEmailChangeProp, ...restComProps } = comProps;
      return (
        <EmailInputWithChange
          record={record}
          index={index}
          onEmailChangeProp={onEmailChangeProp}
          form={form}
          title={title}
          saveFlag={saveFlag}
          isValidating={isValidating}
          restComProps={restComProps}
        />
      );
    })();

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={rules}
          validateFirst={true}
        >
          {isEmailWithOnChange ? emailInputNode : inputNode}
        </Form.Item>
      ) : (
        <div className="text">{children}</div>
      )}
    </td>
  );
};

// MODIFIED: Wrapped with forwardRef for optional ref support
const EditableByRow = forwardRef(
  (
    {
      tableProps,
      dataKey = 'key',
      showAddNewBtn = true,
      handleAdd = () => {},
      deleteRow = () => {},
      setEditInfo = (editInfo) => {},
      saveRow = () => {},
      cancelRow = () => {},
      disableDeletionOfFirstItem = true,
      validatingEmailIndex = null, // NEW: optional prop, defaults to null
      emailValidatedForRow = {}, // optional: row index -> true when domain check passed
      saveRequiresEmailValidation = false, // when true (Client tab), Save disabled until domain check passed
      extraActions = null, // optional extra buttons beside "Add Team Member"
    },
    ref,
  ) => {
    const [form] = Form.useForm();
    const [data, setData] = useState(tableProps.dataSource);
    const [editingKey, setEditingKey] = useState('');
    const isEditing = (record) => record[dataKey] === editingKey;
    const [saveFlag, setSaveFlag] = useState(false);

    useEffect(() => {
      setData(tableProps.dataSource);
    }, [tableProps.dataSource]);

    // NEW: Expose methods via ref - only used when ref is passed
    // Does nothing if ref is not provided (backward compatible)
    useImperativeHandle(ref, () => ({
      // Method to clear a specific field
      clearField: (fieldName, value = '') => {
        form.setFieldsValue({ [fieldName]: value });
      },
      // Method to set multiple fields
      setFields: (values) => {
        form.setFieldsValue(values);
      },
      // Method to get form instance
      getForm: () => form,
      // Method to get current form values
      getFieldsValue: () => form.getFieldsValue(),
      // Method to reset form
      resetFields: () => form.resetFields(),
    }));

    const edit = (record) => {
      form.setFieldsValue({
        ...record,
      });

      setEditingKey(record[dataKey]);
      setEditInfo(record);
    };

    const remove = (record) => {
      deleteRow(record);
    };

    const cancel = () => {
      setEditingKey('');
      setEditInfo(null);
    };

    const save = async (key, row) => {
      const newData = [...data];
      const index = newData.findIndex((item) => key === item[dataKey]);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setData(newData);
        setEditingKey('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey('');
      }
      setEditInfo(null);
    };

    const operationBtnObj = {
      title: 'Action',
      dataIndex: 'operation',
      render: (_, record, index) => {
        const editable = isEditing(record);
        const isRowValidating = validatingEmailIndex === index;
        const isEmailValidated = emailValidatedForRow[index] === true;
        const isSaveDisabled =
          isRowValidating ||
          (saveRequiresEmailValidation && !isEmailValidated);
        return editable ? (
          <span>
            <Typography.Link
              disabled={isSaveDisabled}
              onClick={async () => {
                if (isSaveDisabled) return;
                try {
                  const row = await form.validateFields();
                  await saveRow(row, index);
                  save(record[dataKey], row);
                  setSaveFlag(true);
                } catch (errInfo) {
                  console.log('Validate Failed:', errInfo);
                }
              }}
              style={{
                marginRight: 8,
                ...(isSaveDisabled
                  ? { pointerEvents: 'none', opacity: 0.5 }
                  : {}),
              }}
            >
              Save
            </Typography.Link>
            <Typography.Link
              onClick={() => {
                cancel();
                cancelRow();
              }}
            >
              Cancel
            </Typography.Link>
          </span>
        ) : (
          <span>
            <Typography.Link
              disabled={editingKey !== ''}
              onClick={() => edit(record)}
            >
              Edit
            </Typography.Link>
            <Typography.Link
              style={{ marginLeft: 16 }}
              disabled={
                editingKey !== '' || (disableDeletionOfFirstItem && index == 0)
              }
              onClick={() => remove(record)}
            >
              Delete
            </Typography.Link>
          </span>
        );
      },
    };

    const mergedColumns = [...tableProps.columns, operationBtnObj].map(
      (col) => {
        if (!col.editable) {
          return col;
        }

        return {
          ...col,
          // MODIFIED: Added rowIndex parameter and pass index/validatingEmailIndex to cell
          onCell: (record, rowIndex) => ({
            record,
            inputType: col.inputType,
            customRender: col.customRender,
            comProps: col.comProps,
            saveRow,
            form,
            rules: col.rules,
            dataIndex: col.dataIndex,
            title: col.title,
            editing: isEditing(record),
            saveFlag,
            index: rowIndex, // NEW: pass row index
            validatingEmailIndex, // NEW: pass validating index
          }),
        };
      },
    );

    return (
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          rowClassName={(record, index) =>
            isEditing(record) ? 'editable-row' : ''
          }
          {...tableProps}
          columns={mergedColumns}
        />

        {showAddNewBtn && (
          <div className="font-orange-pwc edit-table-actions">
            <Button
              className="team_member_add_button"
              onClick={() => {
                const newItem = handleAdd();
                form.setFieldsValue({
                  ...newItem,
                });
                setEditingKey(newItem[dataKey]);
                setTimeout(() => {
                  console.log(form.getFieldsValue());
                }, 1000);
              }}
            >
              <PlusCircleFilled className="plus-icon" />{' '}
              <span className="button_text">Add Team Member</span>
            </Button>
            {extraActions}
          </div>
        )}
      </Form>
    );
  },
);

// NEW: Add displayName for better debugging
EditableByRow.displayName = 'EditableByRow';

export default EditableByRow;
